import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import type {
  CreateVenueRequest,
  UpdateVenueRequest,
  UserDto,
  VenueMembershipStats,
} from '@pokerroom/types';
/**
 * @component VenueManagementPage
 * @description 場館管理 — 列表、建立、編輯場館 + 跨場館搜尋 + 報表 (最高管理員 only)
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import VenuePreparationPanel from './VenuePreparationPanel';
import VenueTournamentReportTab from './VenueTournamentReportTab';
import VenueAccountingOverviewTab from './VenueAccountingOverviewTab';

interface Venue {
  id: string;
  name: string;
  code: string;
  edition: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface VenueDetail extends Venue {
  membershipStats: VenueMembershipStats;
  _count: {
    userVenues: number;
    tournaments: number;
    memberVenues: number;
    venueTables: number;
  };
}

type Tab = 'venues' | 'reports' | 'accounting';

/**
 * Single escaped hyphen (not a bare trailing `-`) so the source is unambiguous
 * regardless of position inside the character class, in both the JS check and
 * the native HTML `pattern` attribute.
 */
const VENUE_CODE_PATTERN = '[A-Za-z0-9_\\-]{1,32}';
const VENUE_CODE_REGEX = /^[A-Za-z0-9_-]{1,32}$/;

/** Prefers the backend business message; never renders a raw Error/object/array. */
function extractBusinessErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: unknown } } }).response;
    const message = response?.data?.message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
}

export default function VenueManagementPage() {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const preparationId = searchParams.get('onboarding');
  const revision = useAuthStore((s) => s.authRevision);
  const [tab, setTab] = useState<Tab>('venues');
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateVenueRequest>({
    name: '',
    code: '',
    initialAdministratorId: '',
  });
  const openPreparation = (id: string | null) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (id) next.set('onboarding', id);
      else next.delete('onboarding');
      return next;
    });
  };

  const [codeDrafts, setCodeDrafts] = useState<Record<string, string>>({});
  const [renameDrafts, setRenameDrafts] = useState<Record<string, string>>({});
  // ---- Queries ----
  const { data: venues = [], isLoading } = useQuery<Venue[]>({
    queryKey: ['venues'],
    queryFn: () => api.get<Venue[]>('/venues'),
  });

  const administrators = useQuery({
    queryKey: ['venue-administrator-options', revision],
    queryFn: ({ signal }) => api.get<UserDto[]>('/users', { signal }),
    enabled: showCreate,
    retry: false,
  });

  const { data: detail, isError: detailError } = useQuery<VenueDetail>({
    queryKey: ['venues', editingId],
    retry: false,
    queryFn: () => api.get<VenueDetail>(`/venues/${editingId}`),
    enabled: !!editingId,
  });

  // ---- Mutations ----
  const createMutation = useMutation({
    mutationFn: (data: CreateVenueRequest) => api.post<Venue>('/venues', data),
    onSuccess: async (venue, data) => {
      if (data.initialAdministratorId === useAuthStore.getState().user?.id) {
        await queryClient.cancelQueries();
        queryClient.clear();
        useAuthStore.getState().clearAuth();
        window.location.assign(`/login?venueOnboarding=${encodeURIComponent(venue.id)}`);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      setShowCreate(false);
      setForm({ name: '', code: '', initialAdministratorId: '' });
      openPreparation(venue.id);
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: ['venues'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVenueRequest }) =>
      api.patch(`/venues/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      setEditingId(null);
    },
  });

  const codeMutation = useMutation({
    mutationFn: ({ id, code }: { id: string; code: string }) =>
      api.patch(`/venues/${id}`, { code } satisfies UpdateVenueRequest),
    onSuccess: async (_data, { id }) => {
      setCodeDrafts((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['venues'] }),
        queryClient.invalidateQueries({ queryKey: ['current-venue'] }),
      ]);
    },
  });

  // ---- Tab content renderers ----

  const renderVenuesTab = () => (
    <>
      <AdminPageHeader
        title={t('venue.title')}
        description={t('venue.subtitle')}
        actions={
          <button onClick={() => setShowCreate(true)} className="admin-button admin-button-primary">
            {t('venue.newVenue')}
          </button>
        }
      />

      {preparationId && (
        <VenuePreparationPanel
          key={preparationId}
          venueId={preparationId}
          name={venues.find((v) => v.id === preparationId)?.name ?? preparationId}
          onClose={() => openPreparation(null)}
        />
      )}

      {showCreate && (
        <div className="admin-panel bg-base-200 mb-6">
          <div className="space-y-4">
            <h3 className="font-semibold mb-3">{t('venue.createVenue')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="text"
                placeholder={t('venue.venueName')}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="input input-sm input-bordered"
              />
              <input
                type="text"
                placeholder={t('venue.codePlaceholder')}
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                maxLength={32}
                pattern={VENUE_CODE_PATTERN}
                required
                className="input input-sm input-bordered"
              />
            </div>
            <label className="form-control mt-3">
              <span className="label-text mb-1">{t('venue.initialAdministrator')}</span>
              <select
                aria-label={t('venue.initialAdministrator')}
                value={form.initialAdministratorId}
                className="select select-sm select-bordered"
                disabled={administrators.isPending || administrators.isError}
                onChange={(e) => setForm((f) => ({ ...f, initialAdministratorId: e.target.value }))}
              >
                <option value="">{t('venue.chooseAdministrator')}</option>
                {administrators.data
                  ?.filter((u) => u.isActive)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName} ({u.username})
                    </option>
                  ))}
              </select>
              <span className="text-sm mt-2">{t('venue.administratorGrantHint')}</span>
            </label>
            {administrators.isError && (
              <p role="alert" className="text-error text-sm">
                {t('venue.administratorsFailed')}
              </p>
            )}
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => createMutation.mutate(form)}
                disabled={
                  !form.name ||
                  !form.code ||
                  !form.initialAdministratorId ||
                  administrators.isError ||
                  !VENUE_CODE_REGEX.test(form.code) ||
                  createMutation.isPending
                }
                className="admin-button admin-button-primary "
              >
                {createMutation.isPending ? t('venue.creating') : t('venue.create')}
              </button>
              <button onClick={() => setShowCreate(false)} className="admin-button ">
                {t('venue.cancel')}
              </button>
            </div>
            {createMutation.isError && (
              <p className="text-error text-sm mt-1">
                {extractBusinessErrorMessage(createMutation.error, t('venue.createFailed'))}
              </p>
            )}
            {createMutation.isError && venues.find((v) => v.code === form.code) && (
              <button
                className="admin-button mt-2"
                onClick={() => {
                  const existing = venues.find((v) => v.code === form.code);
                  if (existing) openPreparation(existing.id);
                }}
              >
                {t('venue.resumeExisting')}
              </button>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <span className="loading loading-spinner" />
        </div>
      ) : (
        <div className="space-y-3">
          {venues.map((venue) => (
            <div
              key={venue.id}
              className={`admin-panel bg-base-200 ${!venue.isActive ? 'opacity-50' : ''}`}
            >
              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <div>
                      <h3 className="font-semibold flex flex-wrap items-center gap-2">
                        {venue.name}
                        <span className="badge badge-sm badge-outline">{venue.code}</span>
                        {!venue.isActive && (
                          <span className="badge badge-sm badge-error">{t('venue.inactive')}</span>
                        )}
                      </h3>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="admin-button " onClick={() => openPreparation(venue.id)}>
                      {t('venue.preparation')}
                    </button>
                    <button
                      disabled={codeMutation.isPending}
                      onClick={() => {
                        setEditingId(editingId === venue.id ? null : venue.id);
                        setCodeDrafts({});
                        codeMutation.reset();
                      }}
                      className="admin-button "
                    >
                      {editingId === venue.id ? t('venue.close') : t('venue.detail')}
                    </button>
                  </div>
                </div>
                {editingId === venue.id && detailError && (
                  <p role="alert">{t('venue.statsError')}</p>
                )}
                {editingId === venue.id && detail && (
                  <div className="mt-3 pt-3 border-t border-base-300">
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
                      {[
                        { val: detail._count.userVenues, label: t('venue.users') },
                        { val: detail._count.tournaments, label: t('venue.tournaments') },
                        {
                          val: detail.membershipStats?.associationTotal ?? '—',
                          label: t('venue.associationMembers'),
                        },
                        {
                          val: detail.membershipStats?.venueActiveTotal ?? '—',
                          label: t('venue.activeMembers'),
                        },
                        { val: detail._count.venueTables, label: t('venue.tables') },
                      ].map(({ val, label }) => (
                        <div key={label} className="text-center">
                          <div className="text-lg font-bold">{val}</div>
                          <div className="text-xs text-base-content/50">{label}</div>
                        </div>
                      ))}
                    </div>
                    <p className="mb-3 text-xs text-base-content/70">{t('venue.activityScope')}</p>
                    {detail.membershipStats?.coverage === 'UNVERIFIED' && (
                      <p role="status">{t('venue.activityUnverified')}</p>
                    )}
                    <form
                      className="mb-4 space-y-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const code = codeDrafts[venue.id] ?? venue.code;
                        if (
                          codeMutation.isPending ||
                          code === venue.code ||
                          !VENUE_CODE_REGEX.test(code)
                        )
                          return;
                        if (
                          window.confirm(
                            t('venue.codeImpact', { oldCode: venue.code, newCode: code }),
                          )
                        ) {
                          codeMutation.mutate({ id: venue.id, code });
                        }
                      }}
                    >
                      <label className="form-control">
                        <span className="label-text">{t('venue.codeLabel')}</span>
                        <input
                          aria-label={t('venue.codeLabel')}
                          className="input input-sm input-bordered w-full sm:max-w-md"
                          value={codeDrafts[venue.id] ?? venue.code}
                          maxLength={32}
                          pattern={VENUE_CODE_PATTERN}
                          required
                          disabled={codeMutation.isPending}
                          onChange={(event) => {
                            setCodeDrafts((current) => ({
                              ...current,
                              [venue.id]: event.target.value,
                            }));
                            codeMutation.reset();
                          }}
                        />
                      </label>
                      <p className="text-xs text-base-content/70">{t('venue.codeFormat')}</p>
                      {codeMutation.isError && codeMutation.variables?.id === venue.id && (
                        <p role="alert" className="text-sm text-error">
                          {extractBusinessErrorMessage(
                            codeMutation.error,
                            t('venue.codeSaveFailed'),
                          )}
                        </p>
                      )}
                      {codeMutation.isSuccess && codeMutation.variables?.id === venue.id && (
                        <p role="status" className="text-sm text-success">
                          {t('venue.codeSaved')}
                        </p>
                      )}
                      <button
                        className="admin-button admin-button-primary "
                        type="submit"
                        disabled={
                          codeMutation.isPending ||
                          updateMutation.isPending ||
                          (codeDrafts[venue.id] ?? venue.code) === venue.code ||
                          !VENUE_CODE_REGEX.test(codeDrafts[venue.id] ?? venue.code)
                        }
                      >
                        {t('venue.saveCode')}
                      </button>
                      <button
                        className="admin-button ml-2"
                        type="button"
                        disabled={codeMutation.isPending}
                        onClick={() => {
                          setCodeDrafts((current) => ({ ...current, [venue.id]: venue.code }));
                          codeMutation.reset();
                        }}
                      >
                        {t('venue.cancelCode')}
                      </button>
                    </form>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                      <div className="flex flex-wrap gap-2 w-full sm:max-w-md sm:flex-1">
                        <input
                          value={renameDrafts[venue.id] ?? venue.name}
                          onChange={(event) =>
                            setRenameDrafts((current) => ({
                              ...current,
                              [venue.id]: event.target.value,
                            }))
                          }
                          aria-label={t('venue.renameLabel')}
                          className="input input-xs input-bordered join-item flex-1 min-w-0"
                        />
                        <button
                          className="admin-button admin-button-primary join-item"
                          disabled={
                            updateMutation.isPending ||
                            !(renameDrafts[venue.id] ?? venue.name).trim()
                          }
                          onClick={() =>
                            updateMutation.mutate({
                              id: venue.id,
                              data: { name: (renameDrafts[venue.id] ?? venue.name).trim() },
                            })
                          }
                        >
                          {t('venue.saveName')}
                        </button>
                      </div>
                      {venue.isActive ? (
                        <button
                          onClick={() =>
                            updateMutation.mutate({ id: venue.id, data: { isActive: false } })
                          }
                          disabled={updateMutation.isPending}
                          className="admin-button admin-button-danger"
                        >
                          {t('venue.deactivate')}
                        </button>
                      ) : (
                        <button
                          onClick={() =>
                            updateMutation.mutate({ id: venue.id, data: { isActive: true } })
                          }
                          disabled={updateMutation.isPending}
                          className="admin-button admin-button-success"
                        >
                          {t('venue.activate')}
                        </button>
                      )}
                    </div>
                    {updateMutation.isError && (
                      <p className="text-error text-sm mt-1">
                        {(updateMutation.error as Error)?.message ?? t('venue.updateFailed')}
                      </p>
                    )}
                    <div className="text-xs text-base-content/30 mt-2">
                      ID: {venue.id} | Created: {new Date(venue.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  return (
    <AdminPage className="admin-venues-page">
      {/* Tabs */}
      <div role="tablist" className="admin-tabs">
        <button
          role="tab"
          className={`admin-tab ${tab === 'venues' ? 'admin-tab-active' : ''}`}
          aria-selected={tab === 'venues'}
          onClick={() => setTab('venues')}
        >
          {t('venue.tabVenues')}
        </button>
        <button
          role="tab"
          className={`admin-tab ${tab === 'accounting' ? 'admin-tab-active' : ''}`}
          aria-selected={tab === 'accounting'}
          onClick={() => setTab('accounting')}
        >
          {t('venue.tabAccounting')}
        </button>
        <button
          role="tab"
          className={`admin-tab ${tab === 'reports' ? 'admin-tab-active' : ''}`}
          aria-selected={tab === 'reports'}
          onClick={() => setTab('reports')}
        >
          {t('venue.tabReports')}
        </button>
      </div>

      {tab === 'venues' && renderVenuesTab()}
      {tab === 'accounting' && (
        <VenueAccountingOverviewTab venues={venues.filter((v) => v.isActive)} />
      )}
      {tab === 'reports' && <VenueTournamentReportTab />}
    </AdminPage>
  );
}
